import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  withRouter,
  generatePath,
  useHistory,
  useParams,
  useLocation,
  useRouteMatch
} from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { MenuWrapper } from 'components/menu';
import Panel from 'components/panel/Panel';
import {
  ROUTE_SPORTERS_ENTITY,
  ROUTE_SPORTERS_ENTITY_POOL,
  ROUTE_SPORTERS_ENTITY_SPORTER
} from 'routes/RouteList';
import {
  PanelTitle,
  PanelSubtitle,
  PanelTitleWrapper,
  PanelTitleButton
} from 'components/panel/panel-title';
import PanelHeader from 'components/panel/PanelHeader';
import Icon from 'components/icon/Icon';
import Button from 'components/button/Button';
import messages from 'messages';
import RetryPanel from 'containers/partials/error-boundary/RetryPanel';
import Modal from 'components/modal/Modal';
import ImportSportersCard, {
  IMPORT_SPORTERS_STATE
} from 'containers/partials/cards/ImportSportersCard';
import CopySporterModal, {
  SPORTER_ACTION
} from 'containers/partials/modals/CopySporterModal';
import { StoreContext } from 'index';
import { useGroupsContext } from 'contexts/GroupsContext';
import YearSelect from 'containers/partials/inputs/YearSelect';
import { PanelBody } from 'components/panel';
import useTabIndex from 'hooks/utils/useTabIndex';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import { useMenuContext } from 'contexts/MenuContext';
import PersonsTabs from 'containers/pages/sporters/PersonsTabs';
import RemoveGroupButton from 'containers/partials/buttons/RemoveGroupButton';
import RemovePersonsFromGroupButton from 'containers/partials/buttons/RemovePersonsFromGroupButton';
import InviteSportersButton from 'containers/partials/buttons/InviteSportersButton';
import RemovePersonsFromOrgButton from 'containers/partials/buttons/RemovePersonsFromOrgButton';
import useUserPool from 'hooks/queries/useUserPool';
import AddPersonModal from 'containers/pages/sporters/AddPersonModal';
import GroupModal from 'containers/pages/sporters/GroupModal';
import Group from 'models/Group';
import { ButtonsGroup } from 'components/button';
import { useStore } from 'stores/Store';

const OverView = () => {
  const {
    authStore: {
      user,
      user: { rootEntityId }
    }
  } = useContext(StoreContext);
  const intl = useIntl();
  let location = useLocation();
  const { entityId, groupId, sporterId } = useParams();
  const { push, replace } = useHistory();
  const ability = useAbility(AbilityContext);
  const {
    state: { group, hasGroups, filter },
    actions: { setFilter, setEntities, setActiveGroupId },
    error,
    refetch
  } = useGroupsContext();
  const active = useRouteMatch([
    ROUTE_SPORTERS_ENTITY,
    ROUTE_SPORTERS_ENTITY_POOL
  ]);
  const selected = useStore(state => state.selectedEntities);
  const clearSelected = useStore(state => state.clearSelectedEntities);

  const { addMenuItems, removeAllMenuItems, menuItems } = useMenuContext();
  const { getPool } = useUserPool({
    entityId: rootEntityId
  });
  const [tabIndex, setTabIndex] = useTabIndex(
    [ROUTE_SPORTERS_ENTITY, ROUTE_SPORTERS_ENTITY_POOL],
    false
  );

  const [copyModal, setCopyModal] = useState(false);
  const [addSporterModal, setAddSporterModal] = useState(false);
  const [groupModal, setGroupModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [modalSize, setModalSize] = useState(true);

  useEffect(() => {
    return () => {
      clearSelected();
    };
  }, []);

  useEffect(() => {
    if (entityId === rootEntityId && sporterId && tabIndex > 0) {
      setTabIndex(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    updateContextMenu({ selected });
  }, [tabIndex, selected, group]);

  const onCloseAddSporterModal = reload => {
    if (reload) {
      refetch();
    }
    setAddSporterModal(false);
  };

  const onCloseGroupModal = reload => {
    if (reload) {
      refetch();
    }
    setGroupModal(false);
  };

  const onCloseImportModal = reload => {
    if (reload) {
      refetch();
    }
    setImportModal(false);
  };

  const onImportModalStateChange = state => {
    setModalSize(state !== IMPORT_SPORTERS_STATE.RESULT);
  };

  const onCloseCopyModal = () => {
    setCopyModal(false);
  };

  const onYearFilter = year => {
    setFilter({ year });
  };

  /*const onSelected = persons => {
    setSelected(persons);
  };*/

  const onPersonClickHandler = (item, type) => {
    if (type === 'group') {
      setEntities([]);
      replace(
        generatePath(ROUTE_SPORTERS_ENTITY, { entityId, groupId: item.id })
      );
    } else {
      if (item.id !== sporterId) {
        push(
          generatePath(ROUTE_SPORTERS_ENTITY_SPORTER, {
            entityId,
            groupId,
            sporterId: item.id
          })
        );
      }
    }
  };

  const onBreadcrumbClickedHandler = groupId => {
    push(
      generatePath(ROUTE_SPORTERS_ENTITY, {
        entityId,
        groupId: entityId === groupId ? null : groupId
      })
    );
  };

  const onTabClickHandler = (tab, index) => {
    setTabIndex(index);
    setActiveGroupId(entityId);
    if (tab === 'athletes') {
      push(generatePath(ROUTE_SPORTERS_ENTITY, { entityId }));
    }
    if (tab === 'pool') {
      push(
        generatePath(ROUTE_SPORTERS_ENTITY, {
          entityId,
          groupId: 'all'
        })
      );
    }
  };

  const onPersonsRemovedFromGroup = async () => {
    clearSelected();
  };

  const onPersonsRemovedFromOrg = useCallback(async () => {
    await getPool({
      variables: { entityId: rootEntityId },
      fetchPolicy: 'network-only'
    });
    clearSelected();
    replace(
      generatePath(ROUTE_SPORTERS_ENTITY_POOL, { entityId, groupId: 'all' })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSelected]);

  const updateContextMenu = ({ selected }) => {
    removeAllMenuItems();

    const menuItems = [];
    if (
      groupId &&
      groupId !== 'all' &&
      entityId !== groupId &&
      user.canEditGroups(groupId)
    ) {
      menuItems.push(
        <Button key={'editGroup'} menu onClick={() => setGroupModal('edit')}>
          <Icon id="edit" />
          <FormattedMessage {...messages.menuItemSporterEditGroup} />
        </Button>,

        <RemoveGroupButton
          key={'removeGroup'}
          menu
          entityId={entityId}
          group={group}
          parentId={group.parentId}
        >
          <FormattedMessage {...messages.menuItemSporterRemoveGroup} />
        </RemoveGroupButton>,

        <Button
          key={'importPersons'}
          menu
          onClick={() => setImportModal(true)}
          dataQa={'persons-group-context-import'}
        >
          <FormattedMessage {...messages.buttonSportersImportSporters} />
          <Icon id="add-user" />
        </Button>
      );
    }

    if (selected.length > 0 && tabIndex === 0) {
      menuItems.push(
        <Button
          key={'copyPersons'}
          menu
          onClick={() => setCopyModal(SPORTER_ACTION.COPY)}
        >
          <FormattedMessage {...messages.buttonSportersCopySporters} />
          <Icon id="copy" />
        </Button>,
        <Button
          key={'movePersons'}
          menu
          onClick={() => setCopyModal(SPORTER_ACTION.MOVE)}
        >
          <FormattedMessage {...messages.buttonSportersMoveSporters} />
          <Icon id="move" />
        </Button>,
        <RemovePersonsFromGroupButton
          key={'removepersons'}
          entityId={groupId}
          rootEntityId={entityId}
          ids={selected.map(p => p.id)}
          menu
          onComplete={() => onPersonsRemovedFromGroup(groupId)}
        >
          <FormattedMessage {...messages.menuItemSporterArchiveSporters} />
        </RemovePersonsFromGroupButton>,
        <InviteSportersButton
          key={'invitePersons'}
          menu
          entityId={rootEntityId}
          persons={selected}
        >
          <FormattedMessage {...messages.buttonSportersInvite} />
          <Icon id="email" />
        </InviteSportersButton>
      );
    }
    if (selected.length > 0 && tabIndex === 1) {
      menuItems.push(
        <Button
          key={'copySporters'}
          menu
          onClick={() => setCopyModal(SPORTER_ACTION.COPY_POOL, selected)}
        >
          <FormattedMessage {...messages.buttonSportersCopySportersFromPool} />
          <Icon id="copy" />
        </Button>,
        <RemovePersonsFromOrgButton
          key={'removeSporters'}
          entityId={rootEntityId}
          ids={selected.map(p => p.id)}
          menu
          onComplete={onPersonsRemovedFromOrg}
        >
          <FormattedMessage
            {...messages.menuItemSporterRemoveSporters}
            values={{ count: selected.length }}
          />
        </RemovePersonsFromOrgButton>,
        <InviteSportersButton
          key={'invitePersons'}
          menu
          entityId={rootEntityId}
          persons={selected}
        >
          <FormattedMessage {...messages.buttonSportersInvite} />
          <Icon id="email" />
        </InviteSportersButton>
      );
    }
    if (tabIndex === 1) {
      menuItems.push(
        <Button
          key={'importPersons'}
          menu
          onClick={() => setImportModal(true)}
          dataQa={'persons-pool-context-import'}
        >
          <FormattedMessage {...messages.buttonSportersImportSporters} />
          <Icon id="add-user" />
        </Button>
      );
    }
    addMenuItems(menuItems);
  };

  if (error) return <RetryPanel />;

  return (
    <Panel active={!!active && active.isExact}>
      <PanelHeader>
        <PanelTitleWrapper>
          <PanelTitle>
            <FormattedMessage
              {...messages.titleSporters}
              values={{ inGroup: groupId !== entityId, group: group.name }}
            />
            {((user.rootEntityId !== entityId &&
              ability.can('manage', 'Groups')) ||
              ability.can('update', 'Athletes')) && (
              <MenuWrapper trigger={<PanelTitleButton />}>
                {menuItems}
              </MenuWrapper>
            )}
          </PanelTitle>
          <PanelSubtitle>
            <FormattedMessage {...messages.subtitleSporters} />
          </PanelSubtitle>
        </PanelTitleWrapper>
        <ButtonsGroup
          verticalCenter
          extraClassNames={'u-1-of-1'}
          style={{
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}
        >
          {(user.isSportamundiOrg || user.isHanOrg) && hasGroups && (
            <YearSelect
              extraClassNames={'u-1-of-1'}
              onChange={onYearFilter}
              value={filter.year}
              label={intl.formatMessage(messages.yearPerGroupSelect)}
            />
          )}

          {ability.can('create', 'Athletes') && (
            <Button
              rounded
              primary
              onClick={() => setAddSporterModal(true)}
              dataQa={'add-athlete'}
            >
              <Icon id="add-white" />
              <FormattedMessage {...messages.buttonSportersAddSporter} />
            </Button>
          )}

          {ability.can('create', 'Groups') && (
            <Button
              rounded
              primary
              onClick={() => setGroupModal('add')}
              dataQa={'add-group'}
            >
              <Icon id="add-white" />
              <FormattedMessage {...messages.addGroupButton} />
            </Button>
          )}
        </ButtonsGroup>
      </PanelHeader>
      <PanelBody>
        <PersonsTabs
          entityId={entityId}
          tabIndex={tabIndex}
          onClick={onPersonClickHandler}
          onBreadcrumbClicked={onBreadcrumbClickedHandler}
          onTabClick={onTabClickHandler}
          showPersonStatus={true}
          checkableGroups={false}
        />
        {addSporterModal && (
          <AddPersonModal
            entityId={entityId}
            group={group}
            inGroup={group.id !== entityId}
            onClose={onCloseAddSporterModal}
          />
        )}
        {groupModal && (
          <GroupModal
            entityId={entityId}
            type={groupModal}
            group={
              groupModal === 'edit' ? group : new Group({ parentId: groupId })
            }
            parentGroup={group}
            inGroup={group.id !== entityId}
            onClose={onCloseGroupModal}
          />
        )}
        {copyModal && (
          <CopySporterModal
            oldEntityId={groupId === 'all' ? entityId : groupId}
            onClose={onCloseCopyModal}
            action={copyModal}
          />
        )}
        {importModal && (
          <Modal
            importSporters={!modalSize}
            cardSmall={modalSize}
            autoHeight={modalSize}
            isOpen={true}
            onClose={onCloseImportModal}
          >
            <ImportSportersCard
              rootEntityId={user.rootEntityId}
              entityId={groupId === 'all' ? entityId : groupId}
              entityName={group.name}
              onClose={reload => onCloseImportModal(reload)}
              onStateChange={onImportModalStateChange}
            />
          </Modal>
        )}
      </PanelBody>
    </Panel>
  );
};

export default withRouter(OverView);
